// Dynamic API configuration using environment variables
const getApiBase = () => {
  // Check if we're in test environment first
  if (import.meta.env.MODE === 'test' || typeof global !== 'undefined' && global.fetch && global.fetch._isMockFunction) {
    return '/api';
  }
  
  // If backend URL is explicitly specified
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  if (backendUrl) {
    const apiPrefix = import.meta.env.VITE_API_PREFIX || '/api';
    return `${backendUrl}${apiPrefix}`;
  }
  
  // Get configuration from environment variables
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const apiPrefix = import.meta.env.VITE_API_PREFIX || '/api';
  
  // Check for development hostnames — use relative URL so Vite proxy handles routing
  const devHostnames = (import.meta.env.VITE_DEV_HOSTNAMES || 'localhost,127.0.0.1').split(',');
  if (devHostnames.some(host => hostname === host.trim())) {
    return apiPrefix;
  }
  
  // Check for Coder environment patterns
  const coderPatterns = (import.meta.env.VITE_CODER_PATTERNS || 'coder.,--').split(',');
  console.log('🔍 Checking Coder patterns:', {hostname, coderPatterns});
  if (coderPatterns.some(pattern => hostname.includes(pattern.trim()))) {
    // Replace frontend port with backend port in the URL
    const backendHost = hostname.replace('5173--', '8899--');
    console.log('✅ Coder pattern matched, using:', `${protocol}//${backendHost}${apiPrefix}`);
    return `${protocol}//${backendHost}${apiPrefix}`;
  }
  
  // Check for production hostnames
  const productionHostnames = (import.meta.env.VITE_PRODUCTION_HOSTNAMES || '').split(',');
  if (productionHostnames.some(host => hostname === host.trim())) {
    // For production domains, use relative URLs (nginx proxy)
    return apiPrefix;
  }
  
  // Default fallback for development
  return apiPrefix;
};

const API_BASE = getApiBase();

// Debug logging
console.log('🔧 API Configuration:', {
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  viteApiUrl: import.meta.env.VITE_API_URL,
  viteBackendUrl: import.meta.env.VITE_BACKEND_URL,
  currentHost: window.location.host,
  hostname: window.location.hostname,
  resolvedApiBase: API_BASE
});

class ApiService {
  constructor() {
    // Get storage keys from environment variables
    this.tokenKey = import.meta.env.VITE_ACCESS_TOKEN_KEY || 'token';
    this.refreshTokenKey = import.meta.env.VITE_REFRESH_TOKEN_KEY || 'refreshToken';
    this.userKey = import.meta.env.VITE_USER_DATA_KEY || 'user';
    
    this.token = localStorage.getItem(this.tokenKey);
    
    // Request deduplication to prevent duplicate simultaneous requests
    this.pendingRequests = new Map();
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem(this.tokenKey, token);
  }

  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
    };
  }

  async request(endpoint, options = {}) {
    const fullUrl = `${API_BASE}${endpoint}`;

    // Create a unique key for deduplication (GET requests only)
    const method = options.method || 'GET';
    const requestKey = `${method}:${fullUrl}`;

    // For GET requests, check if there's already a pending request
    if (method === 'GET' && this.pendingRequests.has(requestKey)) {
      console.log('🔄 Reusing pending request:', requestKey);
      return await this.pendingRequests.get(requestKey);
    }

    console.log('🚀 Making API request:', {
      endpoint,
      fullUrl,
      API_BASE,
      options: {
        method,
        headers: {...this.getAuthHeaders(), ...options.headers}
      }
    });

    const requestPromise = this.makeRequest(fullUrl, options, endpoint);

    // Store the promise for deduplication (GET requests only)
    if (method === 'GET') {
      this.pendingRequests.set(requestKey, requestPromise);

      // Clean up after request completes
      requestPromise.finally(() => {
        this.pendingRequests.delete(requestKey);
      });
    }

    return await requestPromise;
  }

  async makeRequest(fullUrl, options, endpoint = '') {
    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...this.getAuthHeaders(),
          ...options.headers,
        },
      });

      console.log('📡 Response received:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        url: response.url
      });

      const responseText = await response.text();

      // Filter sensitive data from logs
      let logText = responseText.substring(0, 200);
      if (endpoint.includes('/otp/')) {
        // Remove OTP codes from logs for security
        logText = logText.replace(/"otp_code":\s*"?\d+"?/gi, '"otp_code":"[REDACTED]"');
      }
      console.log('📄 Response text preview:', logText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('✅ Successfully parsed JSON');
      } catch (parseError) {
        console.error('❌ JSON parse failed:', parseError.message);
        console.error('Raw response:', responseText.substring(0, 500));
        throw new SyntaxError(`Unexpected token '<', "<!doctype "... is not valid JSON`);
      }

      if (!response.ok) {
        // Handle 401 responses (expired/invalid token)
        if (response.status === 401 && this.token) {
          // Try to refresh token automatically
          const refreshToken = localStorage.getItem(this.refreshTokenKey);
          if (refreshToken && !endpoint.includes('/auth/refresh')) {
            try {
              console.log('🔄 Access token expired, attempting auto-refresh...');
              const refreshData = await this.refreshTokens(refreshToken);
              
              // Update tokens
              this.token = refreshData.access_token;
              localStorage.setItem(this.tokenKey, refreshData.access_token);
              if (refreshData.refresh_token) {
                localStorage.setItem(this.refreshTokenKey, refreshData.refresh_token);
              }
              
              // Retry original request with new token (use makeRequest to avoid deduplication)
              console.log('✅ Token refreshed, retrying original request');
              return this.makeRequest(fullUrl, {
                ...options,
                headers: {
                  ...this.getAuthHeaders(),
                  ...options.headers,
                },
              });
            } catch (refreshError) {
              console.error('❌ Token refresh failed:', refreshError);
              this.clearAuth();
              window.dispatchEvent(new CustomEvent('authTokenInvalid'));
            }
          } else {
            this.clearAuth();
            window.dispatchEvent(new CustomEvent('authTokenInvalid'));
          }
        }
        // Extract error message from various possible API error formats
        let errorMessage = data.error || 
                          data.message || 
                          data.detail || 
                          (data.non_field_errors && data.non_field_errors[0]) ||
                          (data.password && data.password[0]) ||
                          (data.phone_number && data.phone_number[0]) ||
                          (data.phone && data.phone[0]) ||
                          (data.name && data.name[0]) ||
                          (data.country && data.country[0]) ||
                          (data.password_confirm && data.password_confirm[0]) ||
                          'Authentication failed. Please check your credentials.';
        
        // If errorMessage is still an object, try to get first error from any field
        if (typeof errorMessage === 'object') {
          const firstKey = Object.keys(data)[0];
          if (firstKey && Array.isArray(data[firstKey])) {
            errorMessage = data[firstKey][0];
          } else if (firstKey) {
            errorMessage = data[firstKey];
          } else {
            errorMessage = 'An error occurred. Please try again.';
          }
        }
        
        // Carry the parsed body along. errorMessage is a single flattened
        // string and its fallback is "Authentication failed", which is wrong
        // for a field validation error; callers that can render field-level
        // messages read error.data instead.
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = data;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  clearAuth() {
    this.token = null;
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
  }

  // Authentication APIs
  async login(credentials) {
    // Map frontend field names to backend expected format
    const backendCredentials = {
      phone_number: credentials.phone,
      password: credentials.password
    };
    
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(backendCredentials),
    });
  }

  async register(userData) {
    // Map frontend field names to backend expected format
    const backendUserData = {
      phone_number: userData.phone,
      password: userData.password,
      password_confirm: userData.password, // Use same password for confirmation
      name: userData.name,
      country: userData.country,
      gender: userData.gender, // Add gender field mapping
    };

    // Address, only when the form supplied one. Bangladesh sends the thana and
    // the server derives its district and division; everywhere else sends a
    // free-text area. Omitted keys are left out entirely rather than sent empty,
    // so a client that does not collect an address is unaffected.
    if (userData.thana) backendUserData.thana = userData.thana;
    if (userData.short_address) backendUserData.short_address = userData.short_address;

    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(backendUserData),
    });
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  async updateProfile(profileData) {
    return this.request('/auth/profile/update', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async getRecentActivity(limit = 100) {
    return this.request(`/auth/profile/activity?limit=${limit}`);
  }

  async getCalendarData(month = null, year = null) {
    const params = [];
    if (month) params.push(`month=${month}`);
    if (year) params.push(`year=${year}`);
    const queryString = params.length ? `?${params.join('&')}` : '';
    return this.request(`/auth/profile/calendar${queryString}`);
  }

  async markWokeUpToday(helperPhone = null) {
    return this.request('/auth/profile/woke-up-today', {
      method: 'POST',
      body: JSON.stringify({ helper_phone: helperPhone }),
    });
  }


  async getDetailedStats() {
    return this.request('/auth/profile/detailed-stats');
  }

  async getAvailabilityStatus() {
    return this.request('/auth/profile/availability');
  }

  async toggleAvailability(isAvailable) {
    return this.request('/auth/profile/availability', {
      method: 'POST',
      body: JSON.stringify({ is_available: isAvailable }),
    });
  }

  async getTodayWakeupStatus() {
    return this.request('/auth/profile/today-status');
  }

  async getAvailableMembersNotWokenUp() {
    return this.request('/auth/available-not-woken-up');
  }


  async refreshTokens(refreshToken) {
    return this.request('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  }

  // OTP APIs
  async sendOtp(phoneNumber) {
    return this.request('/otp/send', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
    });
  }

  async verifyOtp(phoneNumber, otpCode) {
    return this.request('/otp/verify', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber, otpCode }),
    });
  }

  async resendOtp(phoneNumber) {
    return this.request('/otp/resend', {
      method: 'POST',
      body: JSON.stringify({ phoneNumber }),
    });
  }

  async getOtpStatus() {
    return this.request('/otp/status');
  }

  async checkPhoneVerification(phoneNumber) {
    return this.request(`/otp/check?phone=${encodeURIComponent(phoneNumber)}`);
  }

  // Phone-call OTP (outbound callback) — issues a code shown in-app; the user
  // is called and types it on the keypad. Status is polled until verified.
  async initiateCallOtp() {
    return this.request('/otp/call/initiate', { method: 'POST' });
  }

  async getCallOtpStatus() {
    return this.request('/otp/call/status');
  }

  // Password reset via phone call (unauthenticated)
  async initiateResetCall(phoneNumber) {
    return this.request('/otp/call/reset/initiate', {
      method: 'POST',
      body: JSON.stringify({ phone_number: phoneNumber }),
    });
  }

  async getResetStatus(resetHandle) {
    return this.request('/otp/call/reset/status', {
      method: 'POST',
      body: JSON.stringify({ reset_handle: resetHandle }),
    });
  }

  async completeReset(resetHandle, newPassword, confirmPassword) {
    return this.request('/otp/call/reset/complete', {
      method: 'POST',
      body: JSON.stringify({ reset_handle: resetHandle, new_password: newPassword, confirm_password: confirmPassword }),
    });
  }

  // Wake-up System APIs
  async getRandomPhoneNumbers() {
    return this.request('/wakeup/numbers');
  }

  async getRandomPhoneNumber() {
    return this.request('/wakeup/numbers');
  }

  async getWakeUpTimeSettings() {
    // Public endpoint - no authentication required
    const fullUrl = `${API_BASE}/wakeup/time-settings`;
    console.log('🚀 Making public API request:', {
      endpoint: '/wakeup/time-settings',
      fullUrl
    });
    
    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Public response received:', {
        status: response.status,
        statusText: response.statusText
      });

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ JSON parse failed:', parseError.message);
        throw new SyntaxError('Invalid JSON response');
      }

      if (!response.ok) {
        throw new Error(data.message || data.detail || 'Failed to fetch time settings');
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async recordCall(receiverPhone, status) {
    return this.request('/wakeup/record-call', {
      method: 'POST',
      body: JSON.stringify({ receiverPhone, status }),
    });
  }

  // New rate-limited wake-up APIs
  async getRateLimitedNumber() {
    return this.request('/wakeup/get-number');
  }

  async getPendingNumber() {
    return this.request('/wakeup/pending-number');
  }

  async getUserCooldownStatus() {
    return this.request('/wakeup/cooldown-status');
  }

  async updateCallStatus(trackingId, status) {
    return this.request('/wakeup/update-status', {
      method: 'POST',
      body: JSON.stringify({ tracking_id: trackingId, status }),
    });
  }

  async recordWakeUp(wokeByPhone = null) {
    return this.request('/wakeup/wake-up', {
      method: 'POST',
      body: JSON.stringify({ wokeByPhone }),
    });
  }

  async getTodayCallStatus(phoneNumber) {
    return this.request(`/wakeup/status/${encodeURIComponent(phoneNumber)}`);
  }


  async getUserAvailability() {
    return this.request('/wakeup/availability');
  }

  async getMyStats() {
    return this.request('/wakeup/my-stats');
  }

  // Leaderboard APIs
  async getDailyLeaderboard(date = null) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/leaderboard/daily${params}`);
  }

  async getMonthlyLeaderboard(month = null, year = null) {
    const params = [];
    if (month) params.push(`month=${month}`);
    if (year) params.push(`year=${year}`);
    const queryString = params.length ? `?${params.join('&')}` : '';
    return this.request(`/leaderboard/monthly${queryString}`);
  }

  async getAllTimeLeaderboard() {
    return this.request('/leaderboard/all-time');
  }

  async getMyRank() {
    return this.request('/leaderboard/my-rank');
  }

  async getLeaderboardStats() {
    return this.request('/leaderboard/stats');
  }

  async getCountryLeaderboard() {
    return this.request('/leaderboard/countries');
  }

  async getPreviousMonthTopPerformers() {
    return this.request('/leaderboard/previous-month-top');
  }

  // Admin APIs
  async getAdminDashboard() {
    const response = await fetch(`${API_BASE}/admin`, {
      headers: this.getAuthHeaders(),
    });
    return response.text();
  }

  async getAdminUsers() {
    return this.request('/admin/api/users');
  }

  // Community APIs
  async getUserCommunities() {
    return this.request('/auth/communities');
  }

  async createCommunity(name, description = '', isPublic = false) {
    return this.request('/auth/communities', {
      method: 'POST',
      body: JSON.stringify({
        name,
        description,
        is_public: isPublic
      }),
    });
  }

  async addCommunityMember(communityId, phoneNumber) {
    return this.request('/auth/communities/add-member', {
      method: 'POST',
      body: JSON.stringify({ 
        community_id: communityId, 
        phone_number: phoneNumber 
      }),
    });
  }

  async getCommunityRequests() {
    return this.request('/auth/communities/requests');
  }

  async respondToRequest(requestId, action) {
    return this.request('/auth/communities/respond', {
      method: 'POST',
      body: JSON.stringify({ 
        request_id: requestId, 
        action: action 
      }),
    });
  }

  async getCommunityMembers(communityId) {
    return this.request(`/auth/communities/${communityId}/members`);
  }

  async getCommunityAvailableMembers(communityId) {
    return this.request(`/auth/communities/${communityId}/available`);
  }

  async getCommunityNumber(communityId) {
    return this.request('/wakeup/get-community-number', {
      method: 'POST',
      body: JSON.stringify({ community_id: communityId }),
    });
  }

  async getCommunityCallHistory(communityId, phoneNumber) {
    return this.request(`/auth/communities/${communityId}/call-history/${encodeURIComponent(phoneNumber)}`);
  }

  // Members are addressed by id so no phone number travels in a URL, and the
  // full number is fetched only for the one member being called.
  async getCommunityMemberCallHistory(communityId, userId) {
    return this.request(`/auth/communities/${communityId}/members/${userId}/call-history`);
  }

  async getCommunityMemberPhone(communityId, userId) {
    return this.request(`/auth/communities/${communityId}/members/${userId}/phone`);
  }

  async leaveCommunity(communityId) {
    return this.request(`/auth/communities/${communityId}/leave`, {
      method: 'POST',
    });
  }

  async searchPublicCommunities(searchQuery = '') {
    const query = searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : '';
    return this.request(`/auth/communities/search${query}`);
  }

  async joinPublicCommunity(communityId) {
    return this.request(`/auth/communities/${communityId}/join`, {
      method: 'POST',
    });
  }

  async getCommunityByInviteCode(inviteCode) {
    return this.request(`/auth/communities/invite/${inviteCode}`);
  }

  async joinViaInviteCode(inviteCode) {
    return this.request(`/auth/communities/invite/${inviteCode}`, {
      method: 'POST',
    });
  }

  // Call Time Preferences APIs
  async getCallTimePreferences() {
    return this.request('/auth/profile/call-time-preferences');
  }

  async updateCallTimePreferences(preferences) {
    return this.request('/auth/profile/call-time-preferences/update', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // Prayer Times (Salat) APIs
  async getPrayerTimesToday(city = 'Dhaka', country = 'Bangladesh', school = 1) {
    const params = new URLSearchParams({ city, country, school }).toString();
    return this.request(`/salat/today?${params}`);
  }

  async getPrayerTimesByDate(date, city = 'Dhaka', country = 'Bangladesh', school = 1) {
    const params = new URLSearchParams({ city, country, school }).toString();
    return this.request(`/salat/date/${date}?${params}`);
  }

  async getCurrentWakt(city = 'Dhaka', country = 'Bangladesh', school = 1) {
    const params = new URLSearchParams({ city, country, school }).toString();
    return this.request(`/salat/current-wakt?${params}`);
  }

  /**
   * Division -> district -> thana reference data.
   *
   * Public, and the same file the backend filters on, so a place offered here
   * can never be rejected on save. Pass `division` to narrow the districts, or
   * `district` to get that district's thanas — `units` is empty otherwise.
   */
  async getGeo({ division = null, district = null } = {}) {
    const qs = new URLSearchParams();
    if (division) qs.set('division', division);
    if (district) qs.set('district', district);
    const suffix = qs.toString() ? `?${qs}` : '';
    return this.request(`/salat/geo${suffix}`);
  }

  async getDistricts() {
    return this.request('/salat/districts');
  }

  async updatePrayerLocation(district, country) {
    return this.request('/auth/profile/update', {
      method: 'PATCH',
      body: JSON.stringify({
        prayer_time_district: district,
        prayer_time_country: country
      })
    });
  }

  async updateAsrCalculationMethod(method) {
    return this.request('/auth/profile/update', {
      method: 'PATCH',
      body: JSON.stringify({
        asr_calculation_method: method
      })
    });
  }

  async deleteAccount() {
    return this.request('/auth/account/', { method: 'DELETE' });
  }
}

export default new ApiService();